import React, { useState, useEffect, createContext, useContext } from 'react';
import { useTheme } from '../../contexts/ThemeContext';

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = ({ title, message, type = 'info', duration = 5000, action }) => {
    const id = Math.random().toString(36).substr(2, 9);
    const toast = { id, title, message, type, duration, action };
    
    setToasts(prev => [...prev, toast]);

    if (duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }

    return id;
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const removeAllToasts = () => {
    setToasts([]);
  };

  const value = {
    toasts,
    addToast,
    removeToast,
    removeAllToasts,
    // Convenience methods
    success: (title, message, options) => addToast({ title, message, type: 'success', ...options }),
    error: (title, message, options) => addToast({ title, message, type: 'error', duration: 0, ...options }),
    warning: (title, message, options) => addToast({ title, message, type: 'warning', ...options }),
    info: (title, message, options) => addToast({ title, message, type: 'info', ...options }),
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer />
    </ToastContext.Provider>
  );
};

const ToastContainer = () => {
  const { toasts } = useToast();
  const { theme, getSpacing } = useTheme();

  return (
    <div className="toast-container">
      {toasts.map((toast) => (
        <Toast key={toast.id} {...toast} />
      ))}
      
      <style jsx>{`
        .toast-container {
          position: fixed;
          top: ${getSpacing('4')};
          right: ${getSpacing('4')};
          z-index: ${theme.zIndex.toast};
          display: flex;
          flex-direction: column;
          gap: ${getSpacing('2')};
          max-width: 400px;
          width: 100%;
          pointer-events: none;
        }

        @media (max-width: ${theme.breakpoints.sm}) {
          .toast-container {
            left: ${getSpacing('4')};
            right: ${getSpacing('4')};
            max-width: none;
          }
        }
      `}</style>
    </div>
  );
};

const Toast = ({ id, title, message, type, action }) => {
  const { removeToast } = useToast();
  const { theme, getColor, getSpacing, getFontSize } = useTheme();
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    setTimeout(() => setIsVisible(true), 10);
  }, []);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => removeToast(id), 300);
  };

  const getTypeStyles = () => {
    const styles = {
      success: {
        backgroundColor: getColor('success.50'),
        borderColor: getColor('success.200'),
        iconColor: getColor('success.500'),
        titleColor: getColor('success.700'),
        messageColor: getColor('success.600'),
        icon: '✓'
      },
      error: {
        backgroundColor: getColor('error.50'),
        borderColor: getColor('error.200'),
        iconColor: getColor('error.500'),
        titleColor: getColor('error.700'),
        messageColor: getColor('error.600'),
        icon: '✕'
      },
      warning: {
        backgroundColor: getColor('warning.50'),
        borderColor: getColor('warning.200'),
        iconColor: getColor('warning.500'),
        titleColor: getColor('warning.700'),
        messageColor: getColor('warning.600'),
        icon: '⚠'
      },
      info: {
        backgroundColor: getColor('primary.50'),
        borderColor: getColor('primary.200'),
        iconColor: getColor('primary.500'),
        titleColor: getColor('primary.700'),
        messageColor: getColor('primary.600'),
        icon: 'ⓘ'
      }
    };

    return styles[type] || styles.info;
  };

  const typeStyles = getTypeStyles();

  const toastStyles = {
    backgroundColor: typeStyles.backgroundColor,
    border: `1px solid ${typeStyles.borderColor}`,
    borderRadius: theme.borderRadius.lg,
    padding: getSpacing('4'),
    boxShadow: theme.boxShadow.lg,
    transform: `translateX(${isVisible && !isExiting ? '0' : '100%'}) scale(${isVisible && !isExiting ? '1' : '0.95'})`,
    opacity: isVisible && !isExiting ? 1 : 0,
    transition: `all ${theme.animation.duration[300]} ${theme.animation.easing.out}`,
    pointerEvents: 'auto',
    position: 'relative',
    overflow: 'hidden'
  };

  const headerStyles = {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: message ? getSpacing('2') : 0
  };

  const contentStyles = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: getSpacing('3')
  };

  const iconStyles = {
    fontSize: getFontSize('lg'),
    color: typeStyles.iconColor,
    flexShrink: 0,
    marginTop: getSpacing('0.5')
  };

  const textStyles = {
    flex: 1,
    minWidth: 0
  };

  const titleStyles = {
    fontSize: getFontSize('sm'),
    fontWeight: theme.typography.fontWeight.semibold,
    color: typeStyles.titleColor,
    margin: 0,
    lineHeight: theme.typography.lineHeight.tight
  };

  const messageStyles = {
    fontSize: getFontSize('sm'),
    color: typeStyles.messageColor,
    margin: 0,
    lineHeight: theme.typography.lineHeight.normal,
    wordBreak: 'break-word'
  };

  const closeButtonStyles = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: getSpacing('1'),
    borderRadius: theme.borderRadius.sm,
    color: typeStyles.messageColor,
    fontSize: getFontSize('sm'),
    lineHeight: 1,
    flexShrink: 0,
    marginLeft: getSpacing('2'),
    transition: `all ${theme.animation.duration[200]} ${theme.animation.easing.inOut}`,
    '&:hover': {
      backgroundColor: typeStyles.borderColor,
      color: typeStyles.titleColor
    }
  };

  const actionButtonStyles = {
    background: 'none',
    border: `1px solid ${typeStyles.iconColor}`,
    color: typeStyles.iconColor,
    padding: `${getSpacing('1')} ${getSpacing('3')}`,
    borderRadius: theme.borderRadius.sm,
    fontSize: getFontSize('xs'),
    fontWeight: theme.typography.fontWeight.medium,
    cursor: 'pointer',
    marginTop: getSpacing('3'),
    transition: `all ${theme.animation.duration[200]} ${theme.animation.easing.inOut}`,
    '&:hover': {
      backgroundColor: typeStyles.iconColor,
      color: getColor('neutral.0')
    }
  };

  return (
    <div className="toast">
      <div className="toast-header">
        <div className="toast-content">
          <span className="toast-icon">{typeStyles.icon}</span>
          <div className="toast-text">
            {title && <h4 className="toast-title">{title}</h4>}
            {message && <p className="toast-message">{message}</p>}
          </div>
        </div>
        <button className="toast-close" onClick={handleClose} aria-label="Close notification">
          ×
        </button>
      </div>
      
      {action && (
        <button 
          className="toast-action"
          onClick={() => {
            action.onClick();
            handleClose();
          }}
        >
          {action.label}
        </button>
      )}

      <style jsx>{`
        .toast {
          ${Object.entries(toastStyles).map(([key, value]) => 
            `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value};`
          ).join(' ')}
        }

        .toast-header {
          ${Object.entries(headerStyles).map(([key, value]) => 
            `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value};`
          ).join(' ')}
        }

        .toast-content {
          ${Object.entries(contentStyles).map(([key, value]) => 
            `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value};`
          ).join(' ')}
        }

        .toast-icon {
          ${Object.entries(iconStyles).map(([key, value]) => 
            `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value};`
          ).join(' ')}
        }

        .toast-text {
          ${Object.entries(textStyles).map(([key, value]) => 
            `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value};`
          ).join(' ')}
        }

        .toast-title {
          ${Object.entries(titleStyles).map(([key, value]) => 
            `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value};`
          ).join(' ')}
        }

        .toast-message {
          ${Object.entries(messageStyles).map(([key, value]) => 
            `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value};`
          ).join(' ')}
        }

        .toast-close {
          ${Object.entries(closeButtonStyles).map(([key, value]) => {
            if (typeof value === 'object') {
              return '';
            }
            return `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value};`;
          }).join(' ')}
        }

        .toast-close:hover {
          ${closeButtonStyles['&:hover'] ? Object.entries(closeButtonStyles['&:hover']).map(([key, value]) => 
            `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value};`
          ).join(' ') : ''}
        }

        .toast-close:focus {
          outline: 2px solid ${typeStyles.iconColor};
          outline-offset: 1px;
        }

        .toast-action {
          ${Object.entries(actionButtonStyles).map(([key, value]) => {
            if (typeof value === 'object') {
              return '';
            }
            return `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value};`;
          }).join(' ')}
        }

        .toast-action:hover {
          ${actionButtonStyles['&:hover'] ? Object.entries(actionButtonStyles['&:hover']).map(([key, value]) => 
            `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value};`
          ).join(' ') : ''}
        }

        .toast-action:focus {
          outline: 2px solid ${typeStyles.iconColor};
          outline-offset: 1px;
        }

        /* Reduced motion support */
        @media (prefers-reduced-motion: reduce) {
          .toast {
            transition: opacity ${theme.animation.duration[200]} ${theme.animation.easing.inOut};
            transform: none;
          }
        }
      `}</style>
    </div>
  );
};

export default Toast;